







































import { AutoLoadComplete, AutoLoadContext, AutoLoadError } from '@/components/organisms/auto-load/model'
import {
  AutoLoadByFkuIds,
  AutoLoadStrategy,
  AutoLoadWithQuery,
} from '@ajax/vue/components/templates/item-list/paging/strategy/auto-load-strategy'
import { Either } from 'fp-ts/lib/Either'
import _ from 'lodash'
import Vue from 'vue'
import { Component, Prop } from 'vue-property-decorator'

import AppId from '@/util/appid'
import { BrandRouteResolver } from '@/util/brandRouteResolver'

import ItemListAutoLoadable from '@/components/organisms/item-list/AutoLoadable.vue'
import { ListWithPagination } from '@/models/api/response/list'
import { ItemKind } from '@/models/item/kind'
import ItemService from '@/services/api/productclass'
import SelectThumbnailImageBuilder from '@/services/product-image/select-thumbnail-image-builder'

import { log } from '@/log'
import ItemListHeaderAddonBuilder from './item-list/ItemListHeaderAddonBuilder'
import { withoutPagingQuery } from './item-list/paging/query'

@Component({
  components: {
    ItemListAutoLoadable,
  },
})
/**
 * MPAの商品一覧画面 or フリーコンテンツの商品リストから使用される商品一覧表示コンポーネント
 */
class Itemlist extends Vue {

  @Prop({ default: () => ({}) })
  query: { [key: string]: any }

  @Prop({ required: true })
  displayType: string

  @Prop({ default: 24 })
  defaultCount: number

  @Prop({ default: 1 })
  basePage: number

  @Prop({ default: false })
  instaview: boolean

  @Prop({ default: 3 })
  column: number

  @Prop({ default: false })
  instapage: boolean

  @Prop({ default: false })
  dynamicMode: boolean

  @Prop({ default: false })
  counterMode: boolean

  @Prop({ default: 'default' })
  itemKind: ItemKind

  @Prop({ default: false })
  autoLoadEnabled: boolean

  @Prop({ default: false })
  showEmptyMessage: boolean

  @Prop({ default: null })
  displayLevelOfImageToDisplay: number | null

  @Prop({ default: false })
  showItemListHeaderBar: boolean

  @Prop({ default: false })
  hasKeywordSearch: boolean

  @Prop({ default: null })
  ids: null | {
    /** 指定ID全体 */
    all: string[]
    /** サーバーで取得・描画済みのID */
    loadedOnServer: string[],
    /** clientでオートロードする予定のID */
    loadOnClient: string[]
  }

  hasItems: boolean = true

  rootEl: Element | null = null

  mounted() {
    this.rootEl = this.$el
  }

  get headerAddonContainer(): Element {
    // `v-if="showItemListHeaderAddon && rootEl"` によって、
    // この getter が参照される時点での `rootEl` は non-null が保証されている
    const root = this.rootEl as Element

    return root.closest('.gp-itemlist-container') || root
  }

  get appId(): string {
    return AppId.getByBrandName(
      BrandRouteResolver.resolveBrandFromPathElement(this.$route.params.brand_english_name)
    )
  }

  get itemService(): ItemService {
    return new ItemService(this.appId)
  }

  get itemListHeaderAddon(): typeof Vue | null {
    return ItemListHeaderAddonBuilder.create(
      this.itemKind,
      this.displayLevelOfImageToDisplay,
      this.showItemListHeaderBar
    ).build()
  }

  get showItemListHeaderAddon(): boolean {
    if (this.itemListHeaderAddon === null) return false

    // ItemListHeaderBar は0件時でも表示する
    if (this.showItemListHeaderBar) return true

    // ContactLensImageTypeSwitcherFloat は0件時は意味がないため除外する
    return this.hasItems
  }

  get selectThumbnailImageBuilder(): SelectThumbnailImageBuilder {
    if (this.itemKind !== 'contact-lens') {
      return SelectThumbnailImageBuilder.createForApparel(this.instapage)
    }

    // displayLevelOfImageToDisplay が指定されている場合はそちらが優先され、 imageAppKind は無視される
    // またその場合は ItemListHeaderAddon も非表示になる（ ItemListHeaderAddonBuilder 参照）
    return SelectThumbnailImageBuilder.createForContactLens(
      this.instapage,
      this.displayLevelOfImageToDisplay,
      this.$store.getters['contactLensImageType/imageAppKind']
    )
  }

  fetchItemList(baseQuery: object, context: AutoLoadContext): Promise<Either<Error, ListWithPagination<any>>> {
    return this.pagingStrategy.fetchPage(
      {
        ...baseQuery,
        ...withoutPagingQuery(this.query),
      },
      context
    )
  }

  private get pagingStrategy(): AutoLoadStrategy<any> {
    return this.ids === null
      ? new AutoLoadWithQuery({
        productclassService: this.itemService,
      })
      : new AutoLoadByFkuIds({
        ids: this.ids,
        productclassService: this.itemService,
        countPerPage: this.defaultCount,
      })
  }

  onComplete(result: AutoLoadComplete) {
    // result.pagination.total は商品数が0件でも1以上になるケースがある
    // （ AutoLoadByFkuIds がレスポンスを指定したIDの件数で上書きしているため）
    // したがって result.pages.list の全ページを走査して正確な商品数を算出している
    this.hasItems = result.pages.list.some(p => p.count > 0)

    // 非公開商品が含まれているケースを想定し、 hasItems が0件の場合はセクション全体を非表示
    // SEE: https://github.com/Everforth/growthpalette-front/pull/9054#discussion_r3490457470
    const section = this.$el.closest('[data-hide-on-empty]')
    if (section instanceof HTMLElement) {
      section.style.display = this.hasItems ? '' : 'none'
    }
  }

  onError(error: AutoLoadError) {
    log.error(error)
  }
}

export default Itemlist
